import deepEqual from "fast-deep-equal";

import { Locale, LocalizedText } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { ApplicationModel } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";

export interface Item {
	readonly label: LocalizedText;
	readonly activityDescriptor: Activity.Descriptor;
	readonly active?: boolean;
	readonly children?: Item[];
}

/**
 * Filters the modules of the given appmodels and creates items out of them
 * @param locale the current selected locale
 * @param permissions the permissions of the user
 * @returns
 */
export function mapAppmodelsToItems(
	appmodel: ApplicationModel,
	topLevelActivitiesDescriptors: Activity.Descriptor[],
	locale: Locale,
	fachverfahrenModuleIds: string[]
): Item[] {
	const modules = appmodel.content.modules.flatMap(module => mapModule(module));

	return createItemsWithGroupedFachverfahren(fachverfahrenModuleIds, topLevelActivitiesDescriptors, locale, modules);
}

function mapModule(module: ApplicationModel.Module): ApplicationModel.Module | never[] {
	if (!module.menu) {
		return [];
	}

	if (!module.menu.children) {
		return module;
	}

	const children = filterChildMenus(module.menu.children);
	if (!children && !module.menu.initialActivity?.descriptor) {
		return [];
	}

	return {
		...module,
		menu: {
			...module.menu,
			children
		}
	};
}

function filterChildMenus(menus: ApplicationModel.Menu[]): ApplicationModel.Menu[] | undefined {
	const childMenus = menus.flatMap(menu => {
		if (!menu.children) {
			return menu;
		}

		const children = filterChildMenus(menu.children);
		if (!children && !menu.initialActivity?.descriptor) {
			return [];
		}

		return {
			...menu,
			children
		};
	});

	return childMenus.length > 0 ? childMenus : undefined;
}

function createItemsWithGroupedFachverfahren(
	fachverfahrenModuleIds: string[],
	topLevelActivitiesDescriptors: Activity.Descriptor[],
	locale: Locale,
	modules: ApplicationModel.Module[]
): Item[] {
	const fachverfahrenModules: ApplicationModel.Module[] = [];
	const fixedModules = modules.flatMap(module => {
		if (isFachverfahrenModule(module, fachverfahrenModuleIds)) {
			fachverfahrenModules.push(module);
			return [];
		}

		return module;
	});

	const mappedFixedModules = fixedModules.map(module =>
		getItem(topLevelActivitiesDescriptors, module.menu as ApplicationModel.Menu, locale)
	);

	const combinedListIndex = fixedModules.findIndex(x => x.name === "CombinedList_Module");

	if (combinedListIndex < 0) {
		return mappedFixedModules;
	}

	if (fachverfahrenModules.length <= 0) {
		return [...mappedFixedModules.slice(0, combinedListIndex), ...mappedFixedModules.slice(combinedListIndex + 1)];
	}

	const combinedListModule = fixedModules[combinedListIndex];

	const combinedMenu: ApplicationModel.Menu =
		fachverfahrenModules.length === 1
			? ({
					...fachverfahrenModules[0].menu,
					label: [
						{
							locale: "de",
							text: "Fachverfahren"
						}
					],
					name: ""
				} as ApplicationModel.Menu)
			: ({
					name: "GroupedFachverfahren",
					label: [
						{
							locale: "de",
							text: "Fachverfahren"
						}
					],
					children: [combinedListModule, ...fachverfahrenModules].map(module => module.menu)
				} as ApplicationModel.Menu);

	// Depending on the total amount of the filtered "Fachverfahren" the module containing all of them is either
	// a dropdown menu or a single clickable entry.
	// Adds the combinedListModule in front of all the loaded fachverfahren
	const combinedMenuItem = getItem(topLevelActivitiesDescriptors, combinedMenu, locale);

	return [
		...mappedFixedModules.slice(0, combinedListIndex),
		combinedMenuItem,
		...mappedFixedModules.slice(combinedListIndex + 1)
	];
}

/**
 * Creates an item containing all relevant information
 * @param topLevelActivitiesDescriptors
 * @param menu
 * @param locale
 * @returns
 */
function getItem(
	topLevelActivitiesDescriptors: Activity.Descriptor[],
	menu: ApplicationModel.Menu,
	locale: Locale
): Item {
	const label = menu.label;
	const localizedLabel = label && label[0];

	const children = menu.children
		? menu.children.map(child => getItem(topLevelActivitiesDescriptors, child, locale))
		: undefined;

	const active =
		topLevelActivitiesDescriptors.some(activity => deepEqual(activity, menu.initialActivity?.descriptor)) ||
		(children !== undefined && children.some(child => child.active === true));

	return {
		label: localizedLabel || { locale: "de", text: "" },
		activityDescriptor: menu.initialActivity?.descriptor as Activity.Descriptor,
		children,
		active
	};
}

function isFachverfahrenModule(module: ApplicationModel.Module, fachverfahrenModuleIds: string[]): boolean {
	return fachverfahrenModuleIds.includes(module.name);
}
